import { devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Badge, Alert, Spinner, Accordion, Button, Row, Col } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import aiMeetingRecapService from '../services/aiMeetingRecapService';

/**
 * Learner Meeting Recaps Component
 * Displays published meeting recaps for learners to review
 */
const LearnerMeetingRecaps = () => {
  const { currentUser, userProfile } = useAuth();
  const [recaps, setRecaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser && userProfile?.role === 'learner') {
      loadRecaps();
    } else {
      setLoading(false);
    }
  }, [currentUser, userProfile?.role]);

  /**
   * Load published recaps for learner
   */
  const loadRecaps = async () => {
    try {
      setLoading(true);
      setError('');

      const learnerRecaps = await aiMeetingRecapService.getLearnerRecaps(currentUser.uid);
      setRecaps(learnerRecaps);
    } catch (err) {
      devError('Error loading recaps:', err);
      setError('Failed to load meeting recaps. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Track when learner views a recap
   */
  const handleViewRecap = async (recapId) => {
    try {
      await aiMeetingRecapService.trackRecapView(recapId, currentUser.uid);
    } catch (error) {
      devError('Error tracking view:', error);
      // Non-critical, don't show error to user
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /**
   * Get source badge
   */
  const getSourceBadge = (recap) => {
    if (!recap.source) return null;

    const sourceConfig = {
      openai: { bg: 'success', text: 'Created' },
      simulated: { bg: 'warning', text: 'Demo' },
      fallback: { bg: 'secondary', text: 'Fallback' }
    };

    const config = sourceConfig[recap.source] || sourceConfig.simulated;
    return (
      <Badge bg={config.bg} className="ms-2">
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading meeting recaps...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Alert variant="danger" className="mb-0">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (recaps.length === 0) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white">
          <h6 className="mb-0">Meeting Recaps</h6>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-0">
            <strong>No meeting recaps available yet.</strong>
            <p className="mb-0 mt-2">
              Your facilitator will share recaps from your learning sessions here. Check back after your next session!
            </p>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-gradient bg-primary text-white">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Meeting Recaps</h6>
          <Badge bg="light" text="dark">{recaps.length} available</Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Accordion>
          {recaps.map((recap, index) => (
            <Accordion.Item eventKey={index.toString()} key={recap.id}>
              <Accordion.Header onClick={() => handleViewRecap(recap.id)}>
                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                  <div>
                    <strong>{recap.title || 'Learning Session Recap'}</strong>
                    <div className="small text-muted">
                      {formatDate(recap.publishedAt || recap.createdAt)}
                      {recap.module && ` • ${recap.module}`}
                    </div>
                  </div>
                  <div className="text-end">
                    {recap.viewCount > 0 && (
                      <Badge bg="info" className="me-2">
                        {recap.viewCount} views
                      </Badge>
                    )}
                    {getSourceBadge(recap)}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {/* Executive Summary */}
                {recap.recap?.executiveSummary && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Summary</h6>
                    <p className="text-muted">{recap.recap.executiveSummary}</p>
                  </div>
                )}

                {/* Key Learning Points */}
                {recap.recap?.keyLearningPoints && recap.recap.keyLearningPoints.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Key Learning Points</h6>
                    <ul className="mb-0">
                      {recap.recap.keyLearningPoints.map((point, idx) => (
                        <li key={idx} className="mb-1">{point}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Items */}
                {recap.recap?.actionItems && recap.recap.actionItems.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Action Items</h6>
                    <ul className="mb-0">
                      {recap.recap.actionItems.map((item, idx) => (
                        <li key={idx} className="mb-1">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Homework / Follow-up */}
                {recap.recap?.homework && recap.recap.homework.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Homework / Follow-up</h6>
                    <ul className="mb-0">
                      {recap.recap.homework.map((hw, idx) => (
                        <li key={idx} className="mb-1">{hw}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Resources Mentioned */}
                {recap.recap?.resourcesMentioned && recap.recap.resourcesMentioned.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Resources Mentioned</h6>
                    <ul className="mb-0">
                      {recap.recap.resourcesMentioned.map((resource, idx) => (
                        <li key={idx} className="mb-1">{resource}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Next Session Preview */}
                {recap.recap?.nextSessionPreview && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Next Session Preview</h6>
                    <p className="text-muted mb-0">{recap.recap.nextSessionPreview}</p>
                  </div>
                )}

                {/* Attendance Notes */}
                {recap.recap?.attendanceNotes && (
                  <div className="mb-4">
                    <h6 className="text-primary mb-2">Attendance & Engagement</h6>
                    <p className="text-muted mb-0">{recap.recap.attendanceNotes}</p>
                  </div>
                )}

                {/* Metadata */}
                <div className="mt-4 pt-3 border-top">
                  <Row className="small text-muted">
                    <Col md={6}>
                      {recap.facilitator && (
                        <div><strong>Facilitator:</strong> {recap.facilitator}</div>
                      )}
                      {recap.duration && (
                        <div><strong>Duration:</strong> {recap.duration}</div>
                      )}
                    </Col>
                    <Col md={6} className="text-md-end">
                      {recap.recap?.confidence && (
                        <div><strong>Quality Score:</strong> {Math.round(recap.recap.confidence)}%</div>
                      )}
                      {recap.recap?.processingTime && (
                        <div><strong>Processing time:</strong> {recap.recap.processingTime}</div>
                      )}
                    </Col>
                  </Row>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>

        <div className="mt-3 text-center">
          <Button variant="outline-primary" size="sm" onClick={loadRecaps}>
            Refresh Recaps
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default LearnerMeetingRecaps;
